import {
    notEmptyValidator,
    phoneNumberValidator,
    validateCity,
    validateCompanyName,
    validateHouseNo,
    validateNotEmpty,
    validatePhoneNumber,
    validateStreet,
    validateZipCode,
} from '../../components/Validator';
import {
    companyInfoSelector,
    type CompanyInfoState,
    type CompanyInfoStateField,
    TaxEntityType,
} from './companyInfoSlice';
import { Region, regionSelectorWithDefault } from '../region/regionSlice';
import type { RootState } from '../../configuration/setup/store';

export const EuVatValidator = (vat: string): string | null => {
    if (!notEmptyValidator(vat)) {
        return 'registration.error.whitespaceOnly';
    }

    if (!/^[A-Za-z][A-Za-z][A-Za-z0-9]+$/g.test(vat)) {
        return 'registration.error.vatIdNumber.invalidFormat';
    }

    if (vat && vat.length > 15) {
        return 'registration.error.vatIdNumber.maxNumCharacters';
    }

    return null;
};

export const LatamVatValidator = (vat: string, taxEntity: TaxEntityType): string | null => {
    if (!notEmptyValidator(vat)) {
        return 'registration.error.whitespaceOnly';
    }
    switch (taxEntity) {
        case TaxEntityType.NaturalEntity:
            if (/[a-zA-Z]/g.test(vat)) {
                return 'registration.error.vatIdNumber.invalidFormatCPF';
            }
            // typical CPF format: 231.002.999-00
            // we also remove / to be consistent with CNPF
            if (vat.replace(/[.\-/]/g, '').length !== 11) {
                return 'registration.error.vatIdNumber.maxNumCharactersCPF';
            }
            break;
        case TaxEntityType.LegalEntity:
            if (/[a-zA-Z]/g.test(vat)) {
                return 'registration.error.vatIdNumber.invalidFormatCNPJ';
            }
            // typical CNPJ format: 13.339.532/0001-09
            if (vat.replace(/[.\-/]/g, '').length !== 14) {
                return 'registration.error.vatIdNumber.maxNumCharactersCNPJ';
            }
    }
    return null;
};

export const getFieldValidator = (
    fieldName: CompanyInfoStateField,
    state: CompanyInfoState
    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
): ((value: any) => string | null) => {
    switch (fieldName) {
        case 'neighborhood':
        case 'cityId':
        case 'countryCode':
            return validateNotEmpty;
        case 'city':
            return validateCity;
        case 'companyName':
            return validateCompanyName;
        case 'houseNo':
            return validateHouseNo;
        case 'street':
            return validateStreet;
        case 'zipCode':
            return validateZipCode;
        case 'phone':
            if (state.countryCode !== 'BR') {
                return validatePhoneNumber;
            }
            return (value: string) => {
                if (!phoneNumberValidator(value)) {
                    return 'registration.error.phoneNumber.InvalidFormat';
                } else {
                    return null;
                }
            };
        case 'vat':
            if (state.countryCode === 'BR') {
                return (value: string) => {
                    if (LatamVatValidator(value, state.taxEntity) !== null) {
                        // TODO: find out correct error message for invalid VAT in LATAM
                        return 'error';
                    }
                    return null;
                };
            } else {
                return (value: string) => {
                    if (EuVatValidator(value) !== null) {
                        // TODO: find out correct error message for invalid VAT in EU
                        return 'error';
                    }
                    return null;
                };
            }
        case 'taxEntity':
            return (value: TaxEntityType) => {
                if (value !== TaxEntityType.VAT) {
                    return null;
                } else {
                    // TODO: find out correct error message for invalid VAT in EU
                    return 'error';
                }
            };
        default:
            // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
            return (_value: any) => null;
    }
};

export const validateCountryCode = (companyInfoState: CompanyInfoState, region: Region) => {
    if (region === Region.europe) {
        return companyInfoState.countryCode !== 'BR';
    } else {
        return companyInfoState.countryCode === 'BR';
    }
};

export const isCompanyInfoValid = (state: RootState): boolean => {
    const region = regionSelectorWithDefault(state);
    const companyInfoState = companyInfoSelector(state);

    const requiredFields: CompanyInfoStateField[] =
        region === Region.latinamerica ? LATAM_REQUIRED_FIELDS : EUROPE_REQUIRED_FIELDS;

    const allRequiredFieldsValid = requiredFields.every(fieldName => {
        const validator = getFieldValidator(fieldName, companyInfoState);
        return validator(companyInfoState[fieldName]) === null;
    });

    const validCountryCode = validateCountryCode(companyInfoState, region);

    return allRequiredFieldsValid && validCountryCode;
};

const LATAM_REQUIRED_FIELDS: CompanyInfoStateField[] = [
    'neighborhood',
    'companyName',
    'houseNo',
    'phone',
    'street',
    'vat',
    'taxEntity',
    'zipCode',
    'city',
    'cityId',
];

const EUROPE_REQUIRED_FIELDS: CompanyInfoStateField[] = [
    'city',
    'companyName',
    'houseNo',
    'street',
    'vat',
    'zipCode',
    'countryCode',
];
